/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/


export interface TableColumn {
  Header: string;
  accessor: string;
}

export interface SterilizationUsageRecord {
  id: string;
  date: string;
  productOrMaterialName: string;
  batchOrLotOrGirNo: string;
  testName: string;
  loadCycleNo: string;
  analysisStartTime: string;
  tempPressureAttainmentTime: string;
  holdCompletionTime: string;
  analysisEndTime: string;
  noOfReadingsOrActivities: string;
  remarks: string;
  userName: string;
  equipmentId: string;
  equipmentName: string;
  mediaBatchNo: string;
  cycleTemp: string;
  cyclePressure: string;
  holdDurationMinutes: string;
  biologicalIndicator: string;
  cloxIndicator: string;
}

export const columns: TableColumn[] = [
  {
    Header: "tableCommon.sl_no",
    accessor: "slno",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.date",
    accessor: "date",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.productOrMaterialName",
    accessor: "productOrMaterialName",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.batchOrLotOrGirNo",
    accessor: "batchOrLotOrGirNo",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.testName",
    accessor: "testName",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.loadCycleNo",
    accessor: "loadCycleNo",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.analysisStartTime",
    accessor: "analysisStartTime",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.tempPressureAttainmentTime",
    accessor: "tempPressureAttainmentTime",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.holdCompletionTime",
    accessor: "holdCompletionTime",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.analysisEndTime",
    accessor: "analysisEndTime",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.noOfReadingsOrActivities",
    accessor: "noOfReadingsOrActivities",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.remarks",
    accessor: "remarks",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.userName",
    accessor: "userName",
  },
  {
    Header: "sterilizationUsageReport.tableColumns.details",
    accessor: "details",
  },
];

export interface EquipmentOption {
  id: string;
  label: string;
}

export const equipmentOptions: EquipmentOption[] = [
  { id: 'All', label: 'All Equipment' },
  { id: 'EQ-001', label: 'Autoclave Unit A (121°C)' },
  { id: 'EQ-002', label: 'Autoclave Unit B (121°C)' },
  { id: 'EQ-003', label: 'Dry Heat Sterilizer (160°C)' },
  { id: 'EQ-004', label: 'ETO Chamber Unit' }
];

export const mediaBatchOptions: string[] = [
  'All',
  'MB-2026-001',
  'MB-2026-002',
  'MB-2026-003',
  'MB-2026-004',
  'MB-2026-005'
];

export const initialSterilizationUsageData: SterilizationUsageRecord[] = [
  {
    id: 'STR-2026-00001',
    date: '15/10/2026',
    productOrMaterialName: 'Tryptic Soy Broth (TSB) - 500mL',
    batchOrLotOrGirNo: 'MB-2026-001',
    testName: 'Sterilization Validation',
    loadCycleNo: 'LC-2026-0892',
    analysisStartTime: '08:30:15',
    tempPressureAttainmentTime: '08:45:30',
    holdCompletionTime: '09:05:30',
    analysisEndTime: '09:30:45',
    noOfReadingsOrActivities: '12',
    remarks: 'Cycle completed successfully. All BI indicators negative.',
    userName: 'Dr. Sarah Jenkins',
    equipmentId: 'EQ-001',
    equipmentName: 'Autoclave Unit A (121°C)',
    mediaBatchNo: 'MB-2026-001',
    cycleTemp: '121°C',
    cyclePressure: '15 psi',
    holdDurationMinutes: '20',
    biologicalIndicator: 'Negative (Pass)',
    cloxIndicator: 'Color Change Complete (Pass)'
  },
  {
    id: 'STR-2026-00002',
    date: '15/10/2026',
    productOrMaterialName: 'Sabouraud Dextrose Agar (SDA) - 1L',
    batchOrLotOrGirNo: 'MB-2026-002',
    testName: 'Routine Sterilization',
    loadCycleNo: 'LC-2026-0893',
    analysisStartTime: '10:15:00',
    tempPressureAttainmentTime: '10:30:15',
    holdCompletionTime: '10:50:15',
    analysisEndTime: '11:15:30',
    noOfReadingsOrActivities: '8',
    remarks: 'Standard cycle. No deviations observed.',
    userName: 'Michael Chen',
    equipmentId: 'EQ-002',
    equipmentName: 'Autoclave Unit B (121°C)',
    mediaBatchNo: 'MB-2026-002',
    cycleTemp: '121°C',
    cyclePressure: '15 psi',
    holdDurationMinutes: '20',
    biologicalIndicator: 'Negative (Pass)',
    cloxIndicator: 'Color Change Complete (Pass)'
  },
  {
    id: 'STR-2026-00003',
    date: '14/10/2026',
    productOrMaterialName: 'Fluid Thioglycollate Medium (FTM) - 250mL',
    batchOrLotOrGirNo: 'MB-2026-003',
    testName: 'Sterilization Validation',
    loadCycleNo: 'LC-2026-0891',
    analysisStartTime: '14:00:00',
    tempPressureAttainmentTime: '14:15:30',
    holdCompletionTime: '14:35:30',
    analysisEndTime: '15:00:45',
    noOfReadingsOrActivities: '15',
    remarks: 'Validation cycle for new media batch. All parameters within spec.',
    userName: 'Elena Rostova',
    equipmentId: 'EQ-001',
    equipmentName: 'Autoclave Unit A (121°C)',
    mediaBatchNo: 'MB-2026-003',
    cycleTemp: '121°C',
    cyclePressure: '15 psi',
    holdDurationMinutes: '20',
    biologicalIndicator: 'Negative (Pass)',
    cloxIndicator: 'Color Change Complete (Pass)'
  },
  {
    id: 'STR-2026-00004',
    date: '14/10/2026',
    productOrMaterialName: 'Sterile Petri Dishes (90mm)',
    batchOrLotOrGirNo: 'PD-2026-045',
    testName: 'Dry Heat Sterilization',
    loadCycleNo: 'LC-2026-0890',
    analysisStartTime: '09:00:00',
    tempPressureAttainmentTime: '09:30:00',
    holdCompletionTime: '10:30:00',
    analysisEndTime: '11:00:00',
    noOfReadingsOrActivities: '6',
    remarks: 'Dry heat cycle at 160°C for 60 minutes. Completed successfully.',
    userName: 'Alex Morgan',
    equipmentId: 'EQ-003',
    equipmentName: 'Dry Heat Sterilizer (160°C)',
    mediaBatchNo: 'N/A',
    cycleTemp: '160°C',
    cyclePressure: 'Ambient',
    holdDurationMinutes: '60',
    biologicalIndicator: 'N/A',
    cloxIndicator: 'N/A'
  },
  {
    id: 'STR-2026-00005',
    date: '13/10/2026',
    productOrMaterialName: 'Saline Solution 0.9% - 1000mL',
    batchOrLotOrGirNo: 'MB-2026-004',
    testName: 'Routine Sterilization',
    loadCycleNo: 'LC-2026-0889',
    analysisStartTime: '16:30:00',
    tempPressureAttainmentTime: '16:45:15',
    holdCompletionTime: '17:05:15',
    analysisEndTime: '17:30:30',
    noOfReadingsOrActivities: '10',
    remarks: 'Routine sterilization cycle. No issues reported.',
    userName: 'Dr. Sarah Jenkins',
    equipmentId: 'EQ-002',
    equipmentName: 'Autoclave Unit B (121°C)',
    mediaBatchNo: 'MB-2026-004',
    cycleTemp: '121°C',
    cyclePressure: '15 psi',
    holdDurationMinutes: '20',
    biologicalIndicator: 'Negative (Pass)',
    cloxIndicator: 'Color Change Complete (Pass)'
  },
  {
    id: 'STR-2026-00006',
    date: '13/10/2026',
    productOrMaterialName: 'Sterile Syringes (5mL)',
    batchOrLotOrGirNo: 'SYR-2026-012',
    testName: 'ETO Sterilization',
    loadCycleNo: 'LC-2026-0888',
    analysisStartTime: '11:00:00',
    tempPressureAttainmentTime: '11:30:00',
    holdCompletionTime: '13:30:00',
    analysisEndTime: '15:00:00',
    noOfReadingsOrActivities: '4',
    remarks: 'ETO cycle with 2 hour exposure. Aeration completed successfully.',
    userName: 'Michael Chen',
    equipmentId: 'EQ-004',
    equipmentName: 'ETO Chamber Unit',
    mediaBatchNo: 'N/A',
    cycleTemp: '55°C',
    cyclePressure: '600 mmHg',
    holdDurationMinutes: '120',
    biologicalIndicator: 'Negative (Pass)',
    cloxIndicator: 'N/A'
  },
  {
    id: 'STR-2026-00007',
    date: '12/10/2026',
    productOrMaterialName: 'Tryptic Soy Agar (TSA) - 500mL',
    batchOrLotOrGirNo: 'MB-2026-005',
    testName: 'Routine Sterilization',
    loadCycleNo: 'LC-2026-0887',
    analysisStartTime: '08:00:00',
    tempPressureAttainmentTime: '08:15:30',
    holdCompletionTime: '08:35:30',
    analysisEndTime: '09:00:45',
    noOfReadingsOrActivities: '9',
    remarks: 'Standard cycle. Temperature profile within acceptable range.',
    userName: 'Elena Rostova',
    equipmentId: 'EQ-001',
    equipmentName: 'Autoclave Unit A (121°C)',
    mediaBatchNo: 'MB-2026-005',
    cycleTemp: '121°C',
    cyclePressure: '15 psi',
    holdDurationMinutes: '20',
    biologicalIndicator: 'Negative (Pass)',
    cloxIndicator: 'Color Change Complete (Pass)'
  }
];
